import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Phone, ArrowLeft } from "lucide-react";
import { getPaymentStatus } from "../lib/payments";
import { PHONE, PHONE_HREF, LOGO } from "../lib/content";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let count = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (count >= MAX_POLLS) {
          setState("pending");
          return;
        }
        count += 1;
        timer = setTimeout(poll, 2000);
      } catch (e) {
        if (count >= MAX_POLLS) {
          setState("error");
          return;
        }
        count += 1;
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const isInstallment = info?.lookup_key?.endsWith("_x2");

  return (
    <div className="min-h-screen bg-[#1a1410] grain flex flex-col items-center justify-center px-5 py-20">
      <img src={LOGO} alt="B. Thornton Capital & Asset Group" className="h-16 w-auto mb-10" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg bg-zinc-950/70 border border-amber-500/25 p-10 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-amber-500 animate-spin mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-white">Confirming your payment…</h1>
            <p className="mt-3 text-zinc-400 font-light">This will only take a moment.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <CheckCircle2 className="h-16 w-16 text-amber-400 mx-auto" />
            </motion.div>
            <h1 className="mt-6 font-serif text-4xl text-white">Payment Successful</h1>
            <p className="mt-4 text-zinc-300 font-light leading-relaxed">
              Thank you{info?.customer_name ? `, ${info.customer_name}` : ""}! Your
              payment has been received. Our team will reach out to you
              {info?.customer_email ? ` at ${info.customer_email}` : ""} shortly to
              begin your journey.
            </p>
            {isInstallment && (
              <div className="mt-6 bg-amber-500/10 border border-amber-500/30 px-5 py-4 text-left">
                <p className="text-amber-300 text-sm">
                  You've started your <strong>2-payment plan</strong>. Your first
                  installment is complete — your second and final payment will be
                  charged automatically to complete your plan.
                </p>
              </div>
            )}
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="h-14 w-14 text-amber-500 mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-white">Payment Processing</h1>
            <p className="mt-3 text-zinc-400 font-light">
              Your payment is still being confirmed. If it doesn't update shortly,
              please give us a call.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="font-serif text-3xl text-white">We couldn't confirm your payment</h1>
            <p className="mt-3 text-zinc-400 font-light">
              Please contact us and we'll sort it out right away.
            </p>
          </>
        )}

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/"
            data-testid="success-home-link"
            className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 text-[12px] font-bold tracking-[0.14em] uppercase px-6 py-3 hover:bg-amber-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <a
            href={PHONE_HREF}
            className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase text-zinc-300 hover:text-amber-400 transition-colors"
          >
            <Phone className="h-4 w-4 text-amber-500" /> {PHONE}
          </a>
        </div>
      </motion.div>
    </div>
  );
}
